import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackEvent } from '../services/analytics';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Per-route copy. The Airbnb variant targets "airbnb review generator" queries
// and switches the backend prompt to host-to-guest voice via `platform`.
const VARIANTS = {
  google: {
    platform: null,
    url: 'https://stellarreply.com/free-review-response-generator',
    title: 'Free AI Review Response Generator | StellarReply',
    metaDescription: 'Paste any customer review and get a professional, human-sounding reply in seconds. Works for Google, Airbnb and Trustpilot reviews. 100% free, no signup required.',
    ogDescription: 'Generate a professional reply to any customer review in seconds. Free AI tool for local businesses.',
    schemaName: 'Free AI Review Response Generator',
    badge: 'Free tool - no signup required',
    h1Top: 'Free AI review',
    h1Bottom: 'response generator',
    sub: 'Paste any customer review and get a warm, professional reply that sounds like you wrote it, not a robot. Works for Google, Airbnb and Trustpilot reviews.',
    reviewPlaceholder: 'Paste the customer review here...',
    namePlaceholder: 'Business name (optional)',
    showBusinessType: true,
    seoCards: [
      {
        title: 'Why reply to every review?',
        text: 'Businesses that respond to reviews are seen as more trustworthy, and Google has confirmed that responding to reviews improves your local SEO. A thoughtful reply to a negative review can win the customer back, and shows every future reader that you care.',
      },
      {
        title: 'How this generator works',
        text: 'Paste the review, pick a tone, and our AI writes a short, natural-sounding reply in British English. It avoids the corporate cliches ("we appreciate your feedback") that make responses feel templated. For negative reviews it stays calm, apologises sincerely, and moves the conversation offline.',
      },
    ],
    altToolPath: '/free-airbnb-review-response-generator',
    altToolLabel: 'Airbnb host? Try the Airbnb version',
    auditPath: '/audit',
    auditLabel: 'free reputation audit',
    signupPath: '/login?register=true',
    removalText: "Fake, from a competitor or ex-employee, or not about your business? Google removes reviews that break its rules.",
  },
  airbnb: {
    platform: 'airbnb',
    url: 'https://stellarreply.com/free-airbnb-review-response-generator',
    title: 'Free Airbnb Review Response Generator for Hosts | StellarReply',
    metaDescription: 'Paste any Airbnb guest review and get a warm, host-style response in seconds. Free AI tool for Airbnb hosts. No signup required.',
    ogDescription: 'Generate a warm, personal response to any Airbnb guest review in seconds. Free AI tool for hosts.',
    schemaName: 'Free Airbnb Review Response Generator',
    badge: 'Free tool for Airbnb hosts - no signup required',
    h1Top: 'Free Airbnb review',
    h1Bottom: 'response generator',
    sub: 'Paste a guest review and get a warm, personal host response that sounds like you wrote it - not a robot, and not a hotel chain.',
    reviewPlaceholder: 'Paste the guest review here...',
    namePlaceholder: 'Listing or host name (optional)',
    showBusinessType: false,
    seoCards: [
      {
        title: 'Why respond to Airbnb guest reviews?',
        text: 'Your public responses sit on your listing where every future guest reads them. A warm reply to praise reinforces the experience; a calm, professional reply to criticism shows you handle problems well. Airbnb also favours engaged, responsive hosts in search ranking.',
      },
      {
        title: 'How this generator works',
        text: 'Paste the review, pick a tone, and our AI writes a short response in natural hosting language - "guest" and "stay", never "customer". For negative reviews it acknowledges the issue, says what you fixed, and never argues with the guest.',
      },
    ],
    altToolPath: '/free-review-response-generator',
    altToolLabel: 'Not an Airbnb host? Try the general version',
    auditPath: '/free-airbnb-listing-audit',
    auditLabel: 'free Airbnb listing audit',
    // Preselect the hospitality chip on the signup form
    signupPath: '/login?register=true&category=hospitality',
    removalText: "Did the guest threaten a bad review for a refund, or never actually stay? Airbnb removes reviews that break its rules.",
  },
};

// SEO meta tags - set on mount
function useMetaTags(v) {
  useEffect(() => {
    document.title = v.title;
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`) || document.querySelector(`meta[property="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(name.startsWith('og:') ? 'property' : 'name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('description', v.metaDescription);
    setMeta('og:title', v.title);
    setMeta('og:description', v.ogDescription);
    setMeta('og:type', 'website');
    setMeta('og:url', v.url);

    // Self-canonical — the SPA shell ships the homepage canonical, which tells
    // Google this page is a duplicate of /
    const canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical ? canonical.getAttribute('href') : null;
    if (canonical) canonical.setAttribute('href', v.url);

    const schema = document.createElement('script');
    schema.type = 'application/ld+json';
    schema.id = 'generator-schema';
    schema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": v.schemaName,
      "url": v.url,
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web",
      "description": v.ogDescription,
      "offers": { "@type": "Offer", "price": "0", "priceCurrency": "GBP" }
    });
    document.head.appendChild(schema);
    return () => {
      if (canonical && prevCanonical) canonical.setAttribute('href', prevCanonical);
      document.getElementById('generator-schema')?.remove();
    };
  }, [v]);
}

function StarPicker({ rating, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
      {[1, 2, 3, 4, 5].map(i => (
        <span
          key={i}
          onClick={() => onChange(i === rating ? null : i)}
          style={{
            fontSize: 28, cursor: 'pointer', userSelect: 'none',
            color: rating && i <= rating ? '#f59e0b' : '#ddd',
            transition: 'color 0.15s',
          }}
        >&#9733;</span>
      ))}
    </div>
  );
}

const TONES = [
  { value: 'friendly', label: 'Friendly' },
  { value: 'professional', label: 'Professional' },
  { value: 'apologetic', label: 'Apologetic' },
];

export default function FreeReplyGeneratorPage({ variant = 'google' }) {
  const v = VARIANTS[variant];
  const navigate = useNavigate();
  const [reviewText, setReviewText] = useState('');
  const [rating, setRating] = useState(null);
  const [businessName, setBusinessName] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [tone, setTone] = useState('friendly');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [honeypot, setHoneypot] = useState('');
  const [loadedAt] = useState(() => Date.now());

  // Email gate for the copy button
  const [email, setEmail] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [unlocked, setUnlocked] = useState(() => !!localStorage.getItem('sr_free_tool_email'));
  const [unlocking, setUnlocking] = useState(false);
  const [copied, setCopied] = useState(false);

  useMetaTags(v);

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (reviewText.trim().length < 10) return;
    setLoading(true);
    setError('');
    setResult(null);
    setCopied(false);

    try {
      const res = await fetch(`${API_URL}/free-tools/review-reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          review_text: reviewText.trim(),
          star_rating: rating,
          business_name: businessName.trim() || null,
          business_type: businessType.trim() || null,
          tone,
          platform: v.platform,
          website: honeypot,
          loaded_at: loadedAt,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Generation failed. Please try again.');
      }
      setResult(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUnlock = async (e) => {
    e.preventDefault();
    if (!email.includes('@') || !result) return;
    setUnlocking(true);
    try {
      await fetch(`${API_URL}/free-tools/review-reply/${result.id}/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), marketing_consent: marketingConsent }),
      });
      localStorage.setItem('sr_free_tool_email', email.trim());
      setUnlocked(true);
    } catch {
      // Don't block the user on a lead-capture failure
      setUnlocked(true);
    } finally {
      setUnlocking(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(result.reply).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div style={styles.page}>
      <style>{cssText}</style>

      {/* Nav */}
      <nav style={styles.nav}>
        <div style={styles.navInner}>
          <div style={styles.logo} onClick={() => navigate('/')}>
            Stellar<span style={{ color: '#16a34a' }}>Reply</span>
          </div>
          <button style={styles.navCta} onClick={() => navigate(v.signupPath)}>
            Start free trial
          </button>
        </div>
      </nav>

      {/* Hero / Form */}
      <section style={styles.hero}>
        <div style={styles.heroInner}>
          <div style={styles.badge}>{v.badge}</div>
          <h1 style={styles.h1}>
            {v.h1Top}<br />
            <span style={{ color: '#16a34a' }}>{v.h1Bottom}</span>
          </h1>
          <p style={styles.heroSub}>{v.sub}</p>

          <form onSubmit={handleGenerate} style={styles.form}>
            <textarea
              style={{ ...styles.input, minHeight: 120, resize: 'vertical', lineHeight: 1.5 }}
              value={reviewText}
              onChange={e => setReviewText(e.target.value)}
              placeholder={v.reviewPlaceholder}
              maxLength={3000}
              disabled={loading}
            />
            <div style={{ margin: '14px 0' }}>
              <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, marginBottom: 6 }}>
                Star rating (optional)
              </div>
              <StarPicker rating={rating} onChange={setRating} />
            </div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
              <input
                style={{ ...styles.input, flex: '1 1 160px' }}
                type="text"
                value={businessName}
                onChange={e => setBusinessName(e.target.value)}
                placeholder={v.namePlaceholder}
                maxLength={120}
                disabled={loading}
              />
              {v.showBusinessType && (
                <input
                  style={{ ...styles.input, flex: '1 1 160px' }}
                  type="text"
                  value={businessType}
                  onChange={e => setBusinessType(e.target.value)}
                  placeholder="Type, e.g. cafe, salon (optional)"
                  maxLength={100}
                  disabled={loading}
                />
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 16 }}>
              {TONES.map(t => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setTone(t.value)}
                  style={{
                    padding: '8px 18px', borderRadius: 20, fontSize: 13, fontWeight: 600,
                    cursor: 'pointer', fontFamily: "'DM Sans', system-ui, sans-serif",
                    border: tone === t.value ? '1px solid #16a34a' : '1px solid #d4d1cc',
                    background: tone === t.value ? 'rgba(22,163,74,0.08)' : '#fff',
                    color: tone === t.value ? '#16a34a' : '#6b7280',
                  }}
                >{t.label}</button>
              ))}
            </div>
            {/* Honeypot */}
            <input
              type="text" value={honeypot} onChange={e => setHoneypot(e.target.value)}
              autoComplete="off" tabIndex={-1}
              style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0 }}
              aria-hidden="true"
            />
            <button
              style={{
                ...styles.btn,
                opacity: loading || reviewText.trim().length < 10 ? 0.45 : 1,
                cursor: loading || reviewText.trim().length < 10 ? 'not-allowed' : 'pointer',
              }}
              type="submit" disabled={loading || reviewText.trim().length < 10}
            >
              {loading ? 'Writing your reply...' : 'Generate Reply'}
            </button>
            {reviewText.trim().length > 0 && reviewText.trim().length < 10 && (
              <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 8, textAlign: 'center' }}>
                Paste a bit more of the review (at least 10 characters) to generate a reply.
              </div>
            )}
          </form>

          {error && <div style={styles.errorBox} className="audit-fade">{error}</div>}

          {/* Result */}
          {result && (
            <div className="audit-fade" style={{
              marginTop: 24, textAlign: 'left', background: '#fff',
              borderRadius: 12, border: '1px solid #e8e5df', padding: '20px 24px',
            }}>
              <div style={{ fontSize: 11, color: '#16a34a', fontWeight: 700, marginBottom: 8 }}>
                AI Reply by StellarReply
              </div>
              <p style={{ fontSize: 15, color: '#1a1a1a', margin: '0 0 16px', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                {result.reply}
              </p>

              {unlocked ? (
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button style={{ ...styles.btn, width: 'auto', padding: '10px 24px', fontSize: 14 }} onClick={handleCopy}>
                    {copied ? 'Copied!' : 'Copy reply'}
                  </button>
                  <button style={styles.btnGhost} onClick={() => { setResult(null); setReviewText(''); setRating(null); }}>
                    Generate another
                  </button>
                </div>
              ) : (
                <form onSubmit={handleUnlock} style={{
                  background: 'rgba(22,163,74,0.05)', border: '1px solid rgba(22,163,74,0.15)',
                  borderRadius: 10, padding: '14px 16px',
                }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a', marginBottom: 10 }}>
                    Enter your email to copy this reply
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <input
                      style={{ ...styles.input, flex: '1 1 200px', padding: '10px 14px', fontSize: 14 }}
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="you@yourbusiness.com"
                      required
                    />
                    <button
                      type="submit"
                      style={{ ...styles.btn, width: 'auto', padding: '10px 20px', fontSize: 14, opacity: unlocking ? 0.7 : 1 }}
                      disabled={unlocking}
                    >
                      {unlocking ? 'Unlocking...' : 'Unlock copy'}
                    </button>
                  </div>
                  <label style={{
                    display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 10,
                    fontSize: 12, color: '#6b7280', cursor: 'pointer', lineHeight: 1.5,
                  }}>
                    <input
                      type="checkbox"
                      checked={marketingConsent}
                      onChange={e => setMarketingConsent(e.target.checked)}
                      style={{ marginTop: 2, accentColor: '#16a34a' }}
                    />
                    <span>
                      Email me occasional review-response tips and StellarReply offers.
                      Unsubscribe anytime.
                    </span>
                  </label>
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 8 }}>
                    Your email is only used as described in our{' '}
                    <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{ color: '#16a34a' }}>privacy policy</a>.
                    The checkbox is optional - copying works either way.
                  </div>
                </form>
              )}
            </div>
          )}

          {/* Removal check: the person holding a bad review is the one it is for (Oct 2026).
              Plain <a>: /remove-unfair-review is a static page, not a React route. */}
          {result && (rating === null || rating <= 3) && (
            <div className="audit-fade" style={{
              marginTop: 16, textAlign: 'left', background: '#fffbeb',
              border: '1px solid #fde68a', borderRadius: 12, padding: '14px 18px',
            }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a1a', marginBottom: 4 }}>
                Think this review shouldn't be there at all?
              </div>
              <p style={{ fontSize: 13, color: '#4b5563', margin: '0 0 8px', lineHeight: 1.6 }}>
                {v.removalText} Most bad reviews don't qualify, and we'll tell you honestly if yours doesn't.
              </p>
              <a
                href="/remove-unfair-review"
                onClick={() => trackEvent('removal_cta_click', { from: variant === 'airbnb' ? 'airbnb_generator' : 'google_generator' })}
                style={{ fontSize: 14, fontWeight: 700, color: '#16a34a' }}
              >
                Get a free removal check &rarr;
              </a>
            </div>
          )}

          {/* Upgrade CTA under result */}
          {result && (
            <div className="audit-fade" style={{ ...styles.ctaCard, textAlign: 'center', marginTop: 20 }}>
              <h2 style={{ fontSize: 19, fontWeight: 800, margin: '0 0 8px', color: '#1a1a1a' }}>
                Stop copy-pasting. Put replies on autopilot.
              </h2>
              <p style={{ fontSize: 14, color: '#6b7280', margin: '0 0 16px', lineHeight: 1.6 }}>
                StellarReply connects to your Google Business Profile, learns your brand voice,
                and drafts a reply to every new review automatically. Flat &#163;20/month,
                no credits, no per-response charges.
              </p>
              <button style={{ ...styles.btn, width: 'auto', padding: '12px 28px' }} onClick={() => navigate(v.signupPath)}>
                Start 14-day free trial
              </button>
              <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 10 }}>
                No credit card required. Cancel anytime.
              </p>
            </div>
          )}

          {!result && (
            <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 20, textAlign: 'center' }}>
              100% free. Also try our <span onClick={() => navigate(v.auditPath)} style={{ color: '#16a34a', cursor: 'pointer', textDecoration: 'underline' }}>{v.auditLabel}</span>.
              {' '}<span onClick={() => navigate(v.altToolPath)} style={{ color: '#16a34a', cursor: 'pointer', textDecoration: 'underline' }}>{v.altToolLabel}</span>.
            </p>
          )}
        </div>
      </section>

      {/* SEO copy */}
      <section style={{ padding: '20px 20px 60px' }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          {v.seoCards.map(card => (
            <div style={styles.card} key={card.title}>
              <h2 style={styles.cardTitle}>{card.title}</h2>
              <p style={styles.seoText}>{card.text}</p>
            </div>
          ))}
          {variant !== 'airbnb' && (
            <div style={styles.card}>
              <h2 style={styles.cardTitle}>Know where your rating stands</h2>
              <p style={styles.seoText}>
                Replying well protects the rating you have. To see what it would take to move it,
                the{' '}
                <a href="/google-rating-calculator" style={{ color: '#16a34a', fontWeight: 600 }}>
                  Google rating calculator
                </a>{' '}
                tells you how many 5-star reviews your target needs and how many bad ones you can
                absorb, and the{' '}
                <a href="/audit" style={{ color: '#16a34a', fontWeight: 600 }}>
                  free Google review score check
                </a>{' '}
                compares you with nearby competitors.
              </p>
            </div>
          )}
          {variant === 'airbnb' && (
            <div style={styles.card}>
              <h2 style={styles.cardTitle}>Prefer to write your own?</h2>
              <p style={styles.seoText}>
                Grab our ready-made{' '}
                <a href="/blog/airbnb-review-response-templates.html" style={{ color: '#16a34a', fontWeight: 600 }}>
                  Airbnb review response templates for hosts
                </a>{' '}
                or read{' '}
                <a href="/blog/airbnb-review-response-templates.html" style={{ color: '#16a34a', fontWeight: 600 }}>Airbnb Review Response Templates for Hosts</a>. Or see everything{' '}
                <a href="/for-airbnb-hosts" style={{ color: '#16a34a', fontWeight: 600 }}>
                  StellarReply does for Airbnb hosts
                </a>.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer style={styles.footer}>
        <span>Powered by </span>
        <a href="https://stellarreply.com" style={{ color: '#16a34a', textDecoration: 'none', fontWeight: 600 }}>
          StellarReply
        </a>
      </footer>
    </div>
  );
}

const cssText = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&display=swap');
  @keyframes auditFadeIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
  .audit-fade { animation: auditFadeIn 0.5s ease both; }
`;

const styles = {
  page: { fontFamily: "'DM Sans', system-ui, -apple-system, sans-serif", minHeight: '100vh', background: '#faf9f6' },
  nav: { borderBottom: '1px solid #e8e5df', background: '#fff' },
  navInner: { maxWidth: 900, margin: '0 auto', padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  logo: { fontSize: 18, fontWeight: 800, color: '#1a1a1a', cursor: 'pointer' },
  navCta: { background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'DM Sans', system-ui, sans-serif" },
  hero: { padding: '60px 20px 20px', textAlign: 'center' },
  heroInner: { maxWidth: 600, margin: '0 auto' },
  badge: { display: 'inline-block', fontSize: 12, fontWeight: 600, color: '#16a34a', background: 'rgba(22,163,74,0.08)', padding: '4px 14px', borderRadius: 20, marginBottom: 16 },
  h1: { fontSize: 36, fontWeight: 800, color: '#1a1a1a', lineHeight: 1.2, margin: '0 0 16px' },
  heroSub: { fontSize: 15, color: '#6b7280', lineHeight: 1.6, margin: '0 0 32px', maxWidth: 500, marginLeft: 'auto', marginRight: 'auto' },
  form: { maxWidth: 500, margin: '0 auto', textAlign: 'left' },
  input: { width: '100%', padding: '14px 18px', borderRadius: 10, border: '1px solid #d4d1cc', fontSize: 15, fontFamily: "'DM Sans', system-ui, sans-serif", outline: 'none', boxSizing: 'border-box', background: '#fff' },
  btn: { width: '100%', padding: '14px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #16a34a, #059669)', color: '#fff', fontSize: 16, fontWeight: 800, cursor: 'pointer', fontFamily: "'DM Sans', system-ui, sans-serif" },
  btnGhost: { padding: '10px 24px', borderRadius: 10, border: '1px solid #d4d1cc', background: '#fff', color: '#1a1a1a', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans', system-ui, sans-serif" },
  errorBox: { marginTop: 16, padding: '12px 16px', background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.15)', borderRadius: 8, color: '#dc2626', fontSize: 13, maxWidth: 500, marginLeft: 'auto', marginRight: 'auto' },
  card: { background: '#fff', borderRadius: 12, border: '1px solid #e8e5df', padding: '20px 24px', marginBottom: 16 },
  cardTitle: { fontSize: 15, fontWeight: 700, margin: '0 0 12px', color: '#1a1a1a' },
  seoText: { fontSize: 14, color: '#6b7280', lineHeight: 1.7, margin: 0 },
  ctaCard: { background: '#fff', borderRadius: 12, border: '2px solid #16a34a', padding: '28px', marginTop: 8 },
  footer: { textAlign: 'center', padding: '20px', fontSize: 12, color: '#9ca3af', borderTop: '1px solid #e8e5df' },
};
